<template lang="pug">
  .collection-grid(v-if="collection")
    .collection-grid__inner-wrapper
      .collection-grid__header
        h2.collection-grid__title {{ collection.title }}
        .collection-grid__actions
          base-button.collection-grid__sort-button(
            @click="activeModalAction = 'sort'; $refs.actionsModal.open()"
          ) {{ sortButtonLabel }}
      .collection-grid__desktop-filters
        h2.collection-grid__title {{ collection.title }}
        p.collection-grid__filter-title Filter by:
        .collection-grid__filters-list
          .collection-grid__selected-filter(
            v-for="filter in allSelectedFilters"
            :key="filter"
          )
            span.collection-grid__selected-filter-text {{ filter }}
            base-button.collection-grid__selected-filter-remove(
              :label="`Remove ${filter} filter`"
              icon="close"
              icon-size="12px"
              @click="removeFilter(filter)"
            )
        button.collection-grid__clear-filters-button(
          v-if="allSelectedFilters.length > 0"
          @click="clearAllFilters"
        ) Clear All
        accordion.collection-grid__filter-accordion(
          v-for="(filter) in Object.keys(filters)"
          v-if="filters[filter].options.length > 0"
          :key="filter"
          :heading="filterHeading(filter)"
          selected
        )
          label.collection-grid__option.checkbox-input(
            v-for="(option) in alphabetizeFilterList(filters[filter].options)"
          )
            input.checkbox-input__input(type="checkbox" :value="option" v-model="filters[filter].selected")
            span.checkbox-input__checkbox
              icon.checkbox-input__check(
                name="check-white"
                size="8px"
              )
            span.collection-grid__option-text.checkbox-input__label-text {{ option }}
      .collection-grid__grid(v-if="productsWithTile")
        .collection-grid__grid-header
          p.collection-grid__product-count {{ productCountLabel }}
          label.collection-grid__sort-select
            span.collection-grid__sort-select-text Sort by:
            select.collection-grid__sort-select-input(
              v-model="currentSortMethod"
            )
              option(
                v-for="(option) in sortingOptions"
                :key="option.value"
                :value="option.value"
              ) {{ option.label }}
        .collection-grid__filters-list
          button.collection-grid__clear-filters-button(
            v-if="allSelectedFilters.length > 0"
            @click="clearAllFilters"
          ) Clear All
          .collection-grid__selected-filter(
            v-for="filter in allSelectedFilters"
            :key="filter"
          )
            span.collection-grid__selected-filter-text {{ filter }}
            base-button.collection-grid__selected-filter-remove(
              :label="`Remove ${filter} filter`"
              icon="close"
              icon-size="12px"
              @click="removeFilter(filter)"
            )
        transition-group(name="grid-sort-animation" tag="div" class="row small-up-2 large-up-3 hd-up-4" v-if="collection")
          .column(
            v-for="item in currentPageProducts",
            :key="(item.tileType === 'product') ? item.id : 'cta'",
            :class="{ 'column-2x': item.tileType === 'cta' }"
          )
            product-tile(v-if="item.tileType === 'product'", :product="item", :tile-color="tileColor")
            cta-tile(v-else-if="item.tileType === 'cta' && item.backgroundImage"
              :title="item.title"
              :description="item.description"
              :btn-text="item.btnText"
              :btn-link="item.btnUrl"
              :background-image="item.backgroundImage"
              :overlay-opacity="item.overlayOpacity"
            )
      .collection-grid__pagination
        pagination(:total-pages="numPages", @change-page="setPage")

      modal.collection-grid__actions-modal(
        ref="actionsModal"
      )
        template(slot="content")
          .collection-grid__mobile-sort-menu(v-if="activeModalAction === 'sort'")
            h3.collection-grid__mobile-sort-menu-title Sort
            button.collection-grid__mobile-sort-menu-item(
              v-for="(option, index) in sortingOptions"
              :key="`${option.label}__${index}`"
              @click="currentSortMethod = option.value; $refs.actionsModal.close()"
            ) {{ option.label }}
          .collection-grid__mobile-filter-menu(v-else)
            h3.collection-grid__mobile-filter-menu-title Filters
            .collection-grid__mobile-filter-menu-filters
              accordion.collection-grid__filter-accordion(
                v-for="(filter) in Object.keys(filters)"
                v-if="filters[filter].options.length > 0"
                :key="filter"
                :heading="filterHeading(filter)"
              )
                label.collection-grid__option.checkbox-input(
                  v-for="(option) in alphabetizeFilterList(filters[filter].options)"
                )
                  input.checkbox-input__input(type="checkbox" :value="option" v-model="filters[filter].selected")
                  span.checkbox-input__checkbox
                    icon.checkbox-input__check(
                      name="check-white"
                      size="8px"
                    )
                  span.collection-grid__option-text.checkbox-input__label-text {{ option }}
            .collection-grid__mobile-filters-menu-actions
              base-button.collection-grid__mobile-filters-menu-clear.button--outline(
                @click="clearAllFilters"
              ) Clear Filters
              base-button.collection-grid__mobile-filters-menu-apply(
                @click="$refs.actionsModal.close()"
              ) Apply
</template>

<script>
  import { sortProductsBy } from 'scripts/helpers/sort.js';
  import { getFilter } from 'scripts/helpers/tags.js';
  import storeCollection from 'scripts/mixins/storeCollection.js';

  import Pagination from 'scripts/components/basic/Pagination.vue';
  import CtaTile from 'scripts/components/collection/CtaTile.vue';
  import ProductTile from 'scripts/components/product/ProductTile.vue';
  import BaseButton from 'scripts/components/buttons/BaseButton.vue';
  import Modal from 'scripts/components/basic/Modal.vue';
  import Accordion from 'scripts/components/basic/Accordion.vue';
  import Icon from 'scripts/components/basic/Icon.vue';

  export default {
    name: 'CollectionGrid',
    components: {
      Pagination,
      CtaTile,
      ProductTile,
      BaseButton,
      Modal,
      Accordion,
      Icon,
    },
    mixins: [storeCollection],
    props: {
      cta: Object,
      tileColor: String,
      productsPerPage: {
        type: Number,
        default: 24,
      },
    },
    data() {
      return {
        currentPage: 1,
        activeModalAction: 'filters',
        currentSortMethod: 'featured',
        sortingOptions: [
          { label: 'Featured', value: 'featured' },
          { label: 'Price: Low to High', value: 'priceAsc' },
          { label: 'Price: High to Low', value: 'priceDesc' },
          { label: 'Alphabetical: A-Z', value: 'alphabetically' },
          { label: 'Alphabetical: Z-A', value: 'alphabeticallyReverse' },
        ],
        filters: {
          'Product Type': {
            options: [],
            selected: [],
          },
          Concern: {
            title: 'Health Concern',
            options: [],
            selected: [],
          },
          Size: {
            options: [],
            selected: [],
          },
          Ingredient: {
            title: 'Key Ingredient',
            options: [],
            selected: [],
          },
        },
      };
    },
    computed: {
      sortedProducts() {
        return [...this.collection.products].sort(sortProductsBy(this.currentSortMethod)).map((product) => {
          return { ...product, tileType: 'product' };
        });
      },
      filteredProducts() {
        let filteredArr = [...this.sortedProducts];

        Object.keys(this.filters).forEach((filterType) => {
          const selectedFilters = this.filters[filterType].selected;

          if (selectedFilters.length > 0) {
            filteredArr = filteredArr.filter((product) => {
              const optionsTag = getFilter(product.tags, filterType.toLowerCase());
              const options = optionsTag ? optionsTag.split('|') : [];

              if (filterType === 'Product Type') {
                return selectedFilters.includes(product.type);
              }

              return selectedFilters.some((filter) => options.includes(filter));
            });
          }
        });

        return filteredArr;
      },
      productsWithTile() {
        const filteredArr = [...this.filteredProducts];

        if (this.cta) {
          if (filteredArr.length < this.cta.position) {
            filteredArr.push(this.cta);
          } else {
            filteredArr.splice(this.cta.position - 1, 0, this.cta);
          }
        }

        return filteredArr;
      },
      productsByPage() {
        const productsByPage = [];
        let page = 0;
        let startingProductIndexForPage = 0;

        for (page; page <= this.numPages; page += 1) {
          productsByPage[page] = this.productsWithTile.slice(startingProductIndexForPage, startingProductIndexForPage + this.productsPerPage);

          startingProductIndexForPage += this.productsPerPage;
        }

        return productsByPage;
      },
      currentPageProducts() {
        return this.productsByPage[this.currentPage - 1];
      },
      productCountLabel() {
        return `${this.filteredProducts.length} Products`;
      },
      allSelectedFilters() {
        let allSelectedFilters = [];

        Object.keys(this.filters).forEach((filterType) => {
          allSelectedFilters = allSelectedFilters.concat(this.filters[filterType].selected);
        });

        return allSelectedFilters;
      },
      sortButtonLabel() {
        const currentSortOption = this.sortingOptions.find((option) => option.value === this.currentSortMethod);
        return `Sort: ${currentSortOption.label}`;
      },
      numPages() {
        return Math.ceil(this.sortedProducts.length / this.productsPerPage);
      },
      maxProductIndex() {
        return this.currentPage * this.productsPerPage;
      },
      minProductIndex() {
        return this.maxProductIndex - this.productsPerPage + 1;
      },
    },
    watch: {
      collection() {
        const filterTypes = Object.keys(this.filters);

        this.collection.products.forEach((product) => {
          if (product.type && !this.filters['Product Type'].options.includes(product.type)) {
            this.filters['Product Type'].options.push(product.type);
          }

          filterTypes.forEach((filterType) => {
            const options = getFilter(product.tags, filterType.toLowerCase());
            if (options) {
              this.filters[filterType].options = [...new Set([...this.filters[filterType].options, ...options.split('|')])];
            }
          });
        });
      },
    },
    methods: {
      setPage(page) {
        this.currentPage = page;
      },
      removeFilter(filterName) {
        const filterTypes = Object.keys(this.filters);

        filterTypes.forEach((filterType) => {
          const filterIndex = this.filters[filterType].selected.indexOf(filterName);

          if (filterIndex !== -1) {
            this.filters[filterType].selected.splice(filterIndex, 1);
          }
        });
      },
      clearAllFilters() {
        const filterTypes = Object.keys(this.filters);

        filterTypes.forEach((filterType) => {
          this.filters[filterType].selected = [];
        });
      },
      filterHeading(filter) {
        const filterHasValues = this.filters[filter].selected.length > 0;
        const filterTitle = this.filters[filter].title || filter;

        return `${filterTitle}${filterHasValues ? ` (${this.filters[filter].selected.length})` : ''}`;
      },
      alphabetizeFilterList(filterList) {
        if (!filterList) return [];

        return [...filterList].sort();
      },
    },
  };
</script>

<style scoped lang="scss">
  .collection-grid {
    position: relative;
    overflow: hidden;
  }

  .collection-grid__squiggle {
    position: absolute;
    z-index: $z-index-behind-parent;
  }

  .collection-grid__squiggle--top {
    top: $spacing-unit * 24;
    right: -35px;
  }

  .collection-grid__squiggle--bottom {
    bottom: -85px;
    left: 0;
  }

  .collection-grid__inner-wrapper {
    margin: 0 auto;
    padding: $spacing-unit * 4 $spacing-unit * 4 0;
    max-width: $container-max-width;

    @include desktop-up {
      display: flex;
      flex-wrap: wrap;
      padding: $spacing-unit * 10;
    }
  }

  .collection-grid__header {
    display: flex;

    @include desktop-up {
      display: none;
    }
  }

  .collection-grid__grid-header {
    display: flex;
    justify-content: space-between;
    padding: $spacing-unit * 5 0 $spacing-unit * 4 0;
    border-top: 1px solid $color-primary-300;

    @include desktop-up {
      padding: $spacing-unit * 5 0 $spacing-unit * 7 0;
    }
  }

  .collection-grid__product-count {
    font-family: $font-secondary;
    margin: 0;
    padding: 0;
    color: $color-black;
    font-weight: $font-weight-regular;

    @include desktop-up {
      font-size: rem(18);
    }
  }

  .collection-grid__sort-select {
    display: none;
    color: $color-green-900;
    font-size: rem(16);

    @include desktop-up {
      display: block;
    }
  }

  .collection-grid__sort-select-text {
    margin-right: $spacing-unit * 3;
  }

  .collection-grid__sort-select-input {
    padding: 0;
    border: 0;
    border-bottom: 1px solid $color-green-900;
  }

  .collection-grid__title {
    flex: 1 0 33%;
    margin-bottom: $spacing-unit * 6;

    @include desktop-up {
      margin-bottom: 0;
      padding-bottom: $spacing-unit * 8.75;
      font-size: rem(34);
      border-bottom: 1px solid $color-green-900;
    }
  }

  .collection-grid__actions {
    display: flex;
    justify-content: space-between;
    padding-bottom: $spacing-unit * 5;
  }

  .collection-grid__sort-button {
    padding: $spacing-unit * 3;
    min-width: calc(50% - #{$spacing-unit * 6});
    white-space: initial;
  }

  .collection-grid__filter-button {
    flex: 1;
  }

  .collection-grid__desktop-filters {
    display: none;
    width: 312px;

    @include desktop-up {
      margin-right: $spacing-unit * 9;
    }
  }

  .collection-grid__filter-title {
    margin: $spacing-unit * 4 0 $spacing-unit * 6 0;
    color: $color-green-900;
    font-size: rem(16);
  }

  .collection-grid__filters-list {
    .collection-grid__grid & {
      @include desktop-up {
        display: none;
      }
    }
  }

  .collection-grid__selected-filter {
    display: inline-flex;
    align-items: center;
    margin-right: $spacing-unit * 2;
    margin-bottom: $spacing-unit * 2;
    padding: 0 $spacing-unit * 2;
    height: 26px;
    border: 1px solid $color-green-900;
    border-radius: 2px;
  }

  .collection-grid__selected-filter-text {
    margin-right: $spacing-unit * 2;
    color: $color-green-900;
    font-size: rem(16);
  }

  .collection-grid__selected-filter-remove {
    margin: 0;
    padding: 0;
  }

  .collection-grid__clear-filters-button {
    margin: 0 0 $spacing-unit * 4 0;
    padding: 0;
    color: $color-green-900;
    font-size: rem(12);
    font-weight: $font-weight-regular;
    text-transform: uppercase;
    text-decoration: underline;
    background-color: transparent;
    border: 0;

    .collection-grid__grid & {
      margin: $spacing-unit 0 0 0;
      float: right;
    }
  }

  .collection-grid__filter-accordion {
    border-top: 1px solid $color-green-900;

    .collection-grid__mobile-filter-menu & {
      margin: 0 $spacing-unit * 4;

      &:first-child {
        border-top: 0;
      }
    }
  }

  .collection-grid__mobile-filters-menu-actions {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin: 0 $spacing-unit * 4;
  }

  .collection-grid__mobile-filters-menu-apply,
  .collection-grid__mobile-filters-menu-clear {
    width: calc(50% - #{$spacing-unit * 3});
  }

  .collection-grid__option {
    display: flex;
    align-items: center;
    margin-bottom: $spacing-unit * 2;
  }

  .collection-grid__option-text {
    margin-left: $spacing-unit * 2;
    color: $color-green-900;
    font-size: rem(16);
  }

  .collection-grid__grid {
    position: relative;

    @include desktop-up {
      flex: 1;
      margin-top: $spacing-unit * 19;
    }
  }

  .collection-grid__sort-wrapper {
    display: flex;
    justify-content: center;
    margin-bottom: 18px;

    @include tablet-up {
      justify-content: flex-end;
      margin-bottom: 36px;
    }
  }

  .collection-grid__loading {
    display: flex;
    position: absolute;
    width: 100%;
    justify-content: center;
  }

  .collection-grid__pagination {
    width: 100%;
    margin-bottom: $spacing-unit * 2;
    margin-top: $spacing-unit * 10;
    border-top: 1px solid $color-primary-300;
    padding-top: $spacing-unit * 5;
    text-align: center;
    margin-bottom: $spacing-unit * 2;

    @include tablet-up {
      margin-bottom: 50px;
      width: 100%;
    }

    @include desktop-up {
      margin-bottom: $spacing-unit * 2;
      margin-top: $spacing-unit * 10;
      padding-top: $spacing-unit * 5;
      text-align: center;
      margin-bottom: $spacing-unit * 2;
      margin-bottom: 50px;
      width: 100%;
    }
  }

  .collection-grid__mobile-sort-menu-title,
  .collection-grid__mobile-filter-menu-title {
    color: $color-black;
    font-size: rem(18);
    font-weight: $font-weight-medium;
    line-height: 57px;
    text-align: center;
    border-bottom: 1px solid $color-primary-300;
  }

  .collection-grid__mobile-sort-menu-item {
    display: block;
    padding: 0 0 0 $spacing-unit * 4;
    height: 33px;
    font-size: rem(18);
    font-weight: $font-weight-regular;
    background-color: transparent;
    border: 0;
  }
</style>
